<template>
  <form
    id="application-form"
    class="form-renderer"
    :style="styleVars"
    @submit.prevent="onSubmit"
  >
    <div class="inner-wrapper" :style="modulePadding">
      <div class="content-wrapper">
        <SuccessOverlay
          :is-visible="isSubmitSuccessful"
          :title="successMessageTitle"
          :text="successMessageText"
        />
        <GlobalError :error-message="globalError" />
        <div class="form-grid">
          <div
            v-for="field in fields"
            :key="field && field.name"
            :class="['grid-item', fieldColClass(field)]"
          >
            <component
              :is="resolveComponent(field)"
              v-model="formState[field && field.name]"
              :field-config="withDisabledState(field)"
              :error="fieldErrors[field && field.name]"
              @change="onFieldChange(field, $event)"
              @file-selected="onFileSelected(field, $event)"
              @file-cleared="onFileCleared(field, $event)"
            />
          </div>
        </div>
        <div v-if="hasButton" class="form-actions">
          <GlobalUIApplyButton
            v-if="formApplyButton"
            :button="formApplyButton"
            :pending="isSubmitting"
            type="button"
          />
          <button
            v-else
            type="submit"
            class="submit-button"
            :class="buttonClasses"
            :style="buttonStyle"
            :disabled="isSubmitting"
          >
            <Transition name="slide-up" mode="out-in">
              <GlobalUISpinner v-if="isSubmitting" />
              <span v-else>{{ buttonText }}</span>
            </Transition>
          </button>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import TextField from "../TextField/TextField.vue";
import CheckboxField from "../CheckboxField/CheckboxField.vue";
import DatepickerField from "../DatepickerField/DatepickerField.vue";
import UploadField from "../UploadField/UploadField.vue";
import TextareaField from "../TextareaField/TextareaField.vue";
import MultiSelectField from "../MultiSelectField/MultiSelectField.vue";
import GlobalError from "../GlobalError/GlobalError.vue";
import SuccessOverlay from "../SuccessOverlay/SuccessOverlay.vue";
import GlobalUIApplyButton from "../../GlobalUI/GlobalUIApplyButton/GlobalUIApplyButton.vue";
import GlobalUISpinner from "../../GlobalUI/GlobalUISpinner/GlobalUISpinner.vue";
import { EmailSchema, UrlSchema, TelSchema } from "../validators/zodSchemas";
import { globalComputed } from "../../GlobalUI/mixins/global";
import formRendererModule from "./formRendererDefaultModuleProps";

export default {
  name: "FormRenderer",
  components: {
    TextField,
    CheckboxField,
    DatepickerField,
    UploadField,
    TextareaField,
    MultiSelectField,
    GlobalError,
    SuccessOverlay,
    GlobalUIApplyButton,
    GlobalUISpinner,
  },
  mixins: [globalComputed],
  props: {
    module: {
      type: Object,
      default() {
        return formRendererModule;
      },
    },
    formConfig: {
      type: Object,
      default: () => ({
        form: {
          id: "form",
          title: "",
          submitButtonText: "Submit",
          labelColor: "inherit",
          placeholderColor: "#9ca3af",
          formBackground: "transparent",
          fieldBackground: "#ffffff",
          fieldTextColor: "inherit",
          // Optional: global messages for validation
          messages: {
            required: "This field is required",
          },
          asteriskColor: "#d00",
          errorColor: "#d00",
          fields: [],
        },
      }),
    },
    value: {
      type: Object,
      default: null,
    },
    modelValue: {
      type: Object,
      default: undefined,
    },
    validateOnChange: {
      type: Boolean,
      default: true,
    },
    isSubmitSuccessful: {
      type: Boolean,
      default: false,
    },
    globalError: {
      type: String,
      default: "",
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    externalFieldErrors: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  emits: [
    "input",
    "update:modelValue",
    "change",
    "submit",
    "fileSelected",
    "fileCleared",
  ],
  data() {
    const fields = this.extractFields();
    const initial =
      this.modelValue !== undefined ? this.modelValue : this.value;
    return {
      fields,
      formState: this.buildInitialState(fields, initial),
      fieldErrors: {},
      suppressNextModelValidation: false,
    };
  },
  computed: {
    styleVars() {
      const config = this.formConfig && this.formConfig.form;
      return {
        "--cr-form-label-color": this.styleValue(
          config && config.labelColor,
          "inherit",
        ),
        "--cr-form-placeholder-color": this.styleValue(
          config && config.placeholderColor,
          "#9ca3af",
        ),
        "--cr-form-background": this.styleValue(
          config && config.formBackground,
          "transparent",
        ),
        "--cr-form-field-background": this.styleValue(
          config && config.fieldBackground,
          "#ffffff",
        ),
        "--cr-form-field-text-color": this.styleValue(
          config && config.fieldTextColor,
          "inherit",
        ),
        "--cr-form-field-border-color": this.styleValue(
          config && config.fieldBorderColor,
          "#e5e7eb",
        ),
        "--cr-form-asterisk-color": this.styleValue(
          config && config.asteriskColor,
          "#d00",
        ),
        "--cr-form-error-color": this.styleValue(
          config && config.errorColor,
          "#d00",
        ),
      };
    },
    hasButton() {
      const config = this.formConfig && this.formConfig.form;
      return !!(config && (config.applyButton || config.submitButtonText));
    },
    formApplyButton() {
      const config = this.formConfig && this.formConfig.form;
      return config && config.applyButton ? config.applyButton : null;
    },
    applyUrl() {
      const config = this.formConfig && this.formConfig.form;
      const applyButton = config && config.applyButton;
      const linkConfig = applyButton && applyButton.link;
      const urlValue = linkConfig && linkConfig.value;
      return urlValue || "";
    },
    buttonText() {
      const config = this.formConfig && this.formConfig.form;
      const applyButton = config && config.applyButton;
      const text = applyButton && applyButton.text;
      return text || (config && config.submitButtonText) || "Submit";
    },
    buttonStyle() {
      const config = this.formConfig && this.formConfig.form;
      const applyButton = config && config.applyButton;
      const backgroundColor =
        applyButton &&
        applyButton.backgroundColor &&
        applyButton.backgroundColor.value;
      const textColor =
        applyButton && applyButton.textColor && applyButton.textColor.value;
      const fillStyle =
        applyButton && applyButton.fillStyle && applyButton.fillStyle.value; // 'fill' | 'outline'
      const buttonStyleObject = {};
      if (fillStyle === "outline") {
        buttonStyleObject.background = "transparent";
        buttonStyleObject.border = "1px solid " + (backgroundColor || "#ccc");
        buttonStyleObject.color = textColor || backgroundColor || "inherit";
      } else {
        buttonStyleObject.background = backgroundColor || "#fff";
        buttonStyleObject.border = "1px solid " + (backgroundColor || "#ccc");
        buttonStyleObject.color = textColor || "inherit";
      }
      return buttonStyleObject;
    },
    buttonClasses() {
      const config = this.formConfig && this.formConfig.form;
      const applyButton = config && config.applyButton;
      const corners =
        applyButton && applyButton.corners && applyButton.corners.value; // 'round' | 'square'
      if (corners === "round") return "-round";
      return "-square";
    },
    successMessageTitle() {
      const config = this.formConfig && this.formConfig.form;
      const messages = config && config.messages;
      return (messages && messages.successTitle) || "Success!";
    },
    successMessageText() {
      const config = this.formConfig && this.formConfig.form;
      const messages = config && config.messages;
      return (
        (messages && messages.successText) ||
        "Your application has been submitted successfully."
      );
    },
  },
  watch: {
    value(newVal) {
      if (this.modelValue !== undefined) return;
      this.formState = this.buildInitialState(this.fields, newVal);
      if (this.validateOnChange) {
        this.validateAll();
      } else {
        this.fieldErrors = {};
      }
    },
    modelValue(newVal) {
      if (this.suppressNextModelValidation) {
        this.suppressNextModelValidation = false;
        this.formState = this.buildInitialState(this.fields, newVal);
        return;
      }
      this.formState = this.buildInitialState(this.fields, newVal);
      if (this.validateOnChange) {
        this.validateAll();
      } else {
        this.fieldErrors = {};
      }
    },
    formConfig: {
      handler() {
        this.fields = this.extractFields();
        this.formState = this.buildInitialState(this.fields, this.formState);
        if (this.validateOnChange) {
          this.validateAll();
        } else {
          this.fieldErrors = {};
        }
      },
      deep: true,
    },
    externalFieldErrors: {
      handler(newErrors) {
        const merged = Object.assign({}, this.fieldErrors);
        if (newErrors && typeof newErrors === "object") {
          for (const key in newErrors) {
            if (Object.prototype.hasOwnProperty.call(newErrors, key)) {
              merged[key] = newErrors[key] || "";
            }
          }
        }
        this.fieldErrors = merged;
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    styleValue(v, fallback) {
      return v !== undefined && v !== null && String(v).length > 0
        ? String(v)
        : fallback;
    },
    extractFields() {
      const config = this.formConfig && this.formConfig.form;
      const fieldsArray = config && config.fields ? config.fields : [];
      return Array.isArray(fieldsArray) ? fieldsArray : [];
    },
    buildInitialState(fields, incomingValues) {
      const initialState = {};
      if (incomingValues && typeof incomingValues === "object") {
        for (const key in incomingValues) {
          if (Object.prototype.hasOwnProperty.call(incomingValues, key))
            initialState[key] = incomingValues[key];
        }
      }
      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        if (!(field && field.name)) continue;
        if (initialState[field.name] === undefined)
          initialState[field.name] = this.defaultValueForField(field);
      }
      return initialState;
    },
    defaultValueForField(field) {
      if (!field || !field.type) return null;
      if (field.type === "checkbox") return false;
      if (field.type === "upload") return null;
      if (field.type === "multiselect") return [];
      return "";
    },
    requiredMessage(field) {
      const requiredFromField =
        field && field.messages && field.messages.required;
      const config = this.formConfig && this.formConfig.form;
      const requiredFromConfig =
        config && config.messages && config.messages.required;
      const fallbackMessage = config && config.requiredMessage;
      return (
        requiredFromField ||
        requiredFromConfig ||
        fallbackMessage ||
        "This field is required"
      );
    },
    resolveComponent(field) {
      if (!field || !field.type) return TextField;
      if (field.type === "text") return TextField;
      if (field.type === "checkbox") return CheckboxField;
      if (field.type === "datepicker") return DatepickerField;
      if (field.type === "upload") return UploadField;
      if (field.type === "textarea") return TextareaField;
      if (field.type === "multiselect") return MultiSelectField;
      return TextField;
    },
    withId(field) {
      if (!field) return field;
      if (field.id) return field;
      return Object.assign({}, field, { id: "field-" + field.name });
    },
    withDisabledState(field) {
      const fieldWithId = this.withId(field);
      if (!fieldWithId) return fieldWithId;
      return Object.assign({}, fieldWithId, {
        disabled: this.isSubmitting || fieldWithId.disabled,
      });
    },
    onFieldChange(field, value) {
      if (!(field && field.name)) return;
      this.formState[field.name] = value;
      if (this.validateOnChange) {
        const errorMessage = this.validateField(field, value);
        if (this.$set) {
          this.$set(this.fieldErrors, field.name, errorMessage);
        } else {
          this.fieldErrors = Object.assign({}, this.fieldErrors, {
            [field.name]: errorMessage,
          });
        }
      } else {
        // Do not show errors while typing when validateOnChange is false
        if (this.$set) {
          this.$set(this.fieldErrors, field.name, "");
        } else {
          this.fieldErrors = Object.assign({}, this.fieldErrors, {
            [field.name]: "",
          });
        }
      }
      // Prevent our own v-model echo from triggering validateAll via the modelValue watcher
      this.suppressNextModelValidation = true;
      this.$emit("input", this.formState);
      this.$emit("update:modelValue", this.formState);
      this.$emit("change", {
        name: field.name,
        value,
        values: this.formState,
        isValid: this.isFormValid(),
        errors: this.fieldErrors,
      });
    },
    validateAll() {
      const errors = {};
      for (let i = 0; i < this.fields.length; i++) {
        const field = this.fields[i];
        if (!(field && field.name)) continue;
        errors[field.name] = this.validateField(
          field,
          this.formState[field.name],
        );
      }
      this.fieldErrors = errors;
    },
    validateField(field, value) {
      if (!field) return "";
      const isRequired = !!(field && field.required);
      const inputType = field && field.inputType;
      const requiredMessageText = this.requiredMessage(field);
      const fieldMessages = (field && field.messages) || {};
      const globalMessages =
        (this.formConfig &&
          this.formConfig.form &&
          this.formConfig.form.messages) ||
        {};

      // Required checks
      if (isRequired) {
        if (field.type === "checkbox") return value ? "" : requiredMessageText;
        if (field.type === "upload") return value ? "" : requiredMessageText;
        if (field.type === "multiselect")
          return Array.isArray(value) && value.length > 0
            ? ""
            : requiredMessageText;
        if (value === undefined || value === null) return requiredMessageText;
        if (typeof value === "string" && value.trim().length === 0)
          return requiredMessageText;
      }

      // Email format validation (only when a value is provided)
      if (inputType === "email") {
        if (value === undefined || value === null) return "";
        const stringValue = String(value).trim();
        if (stringValue.length === 0) return "";
        const parsed = EmailSchema.safeParse(stringValue);
        if (!parsed.success)
          return (
            fieldMessages.emailInvalid ||
            globalMessages.emailInvalid ||
            "Please enter a valid email address"
          );
      }

      // URL format validation (only when a value is provided)
      if (inputType === "url") {
        if (value === undefined || value === null) return "";
        const stringValue = String(value).trim();
        if (stringValue.length === 0) return "";
        const parsed = UrlSchema.safeParse(stringValue);
        if (!parsed.success)
          return (
            fieldMessages.urlInvalid ||
            globalMessages.urlInvalid ||
            "Please enter a valid URL"
          );
      }

      // Telephone format validation (only when a value is provided)
      if (inputType === "tel") {
        if (value === undefined || value === null) return "";
        const stringValue = String(value).trim();
        if (stringValue.length === 0) return "";
        const parsed = TelSchema.safeParse(stringValue);
        if (!parsed.success)
          return (
            fieldMessages.telInvalid ||
            globalMessages.telInvalid ||
            "Please enter a valid phone number"
          );
      }

      // Textarea length validation (only when a value is provided)
      if (field.type === "textarea") {
        const min = typeof field.minLength === "number" ? field.minLength : 0;
        const max =
          typeof field.maxLength === "number" ? field.maxLength : null;
        if (value === undefined || value === null) return "";
        const stringValue = String(value);
        if (min && stringValue.length < min) {
          const template =
            fieldMessages.minLength ||
            globalMessages.minLength ||
            "Please enter at least {min} characters";
          return String(template).replace("{min}", String(min));
        }
        if (max !== null && stringValue.length > max) {
          const template =
            fieldMessages.maxLength ||
            globalMessages.maxLength ||
            "Please stay under {max} characters";
          return String(template).replace("{max}", String(max));
        }
      }

      return "";
    },
    isFormValid() {
      for (const k in this.fieldErrors) {
        if (Object.prototype.hasOwnProperty.call(this.fieldErrors, k)) {
          if (this.fieldErrors[k]) return false;
        }
      }
      return true;
    },
    onSubmit() {
      this.validateAll();
      const valid = this.isFormValid();
      this.$emit("submit", {
        valid,
        values: this.formState,
        errors: this.fieldErrors,
      });
    },
    onFileSelected(field, payload) {
      const file = payload && payload.file;
      const name = field && field.name;
      this.$emit("fileSelected", {
        file: file,
        name: name,
        field: field,
        values: this.formState,
      });
    },
    onFileCleared(field) {
      const name = field && field.name;
      this.$emit("fileCleared", {
        name: name,
        field: field,
        values: this.formState,
      });
    },
    fieldColClass(field) {
      const widthOption = field && field.width ? String(field.width) : "FULL";
      if (widthOption === "HALF") return "col-half";
      return "col-full";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/grid";
@import "../../../assets/scss/abstracts/mixins";

.form-renderer {
  position: relative;

  container-type: inline-size;
  display: flex;
  justify-content: center;
  background: var(--cr-form-background);
}

.inner-wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  justify-content: center;

  width: 100%;
  max-width: 1600px;
  padding-block: 25px;
  padding-inline: var(--cr-spacing-l);

  @include mobile {
    padding-inline: var(--cr-spacing-xs);
  }
}

.content-wrapper {
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 25px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 25px 12px;

  @include mobile {
    grid-template-columns: 1fr;
  }
}

.form-actions {
  :deep(.apply-button) {
    width: 100%;
  }
}

.submit-button {
  cursor: pointer;

  padding: 8px 12px;

  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.submit-button.-round {
  padding-inline: 18px;
  border-radius: 9999px;
}

.submit-button.-square {
  border-radius: 4px;
}
</style>

<style lang="scss">
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 250ms ease-in-out;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.slide-up-enter-to,
.slide-up-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
